import { AppstoreFilled } from '@ant-design/icons';
import { Badge, Button, Col, DatePicker, Input, Popover, Radio, Row, Select, Space } from 'antd';
import { List } from 'lucide-react';
import React from 'react';
import type { Employee } from '../../models/Employee';
import type { BoardFilters } from './useBoardFilters';
import { rangeParam, rangeValue } from './useBoardFilters';
import { priorityOptions } from './useTaskHelpers';

const { Option } = Select;

export type BoardToolbarProps = {
  filters: BoardFilters;
  setFilter: (key: keyof BoardFilters, value: string | undefined) => void;
  clearFilters: () => void;
  activeCount: number;
  /** Assignee options; empty on the public board, which has no user list. */
  users: Employee[];
  canExport: boolean;
  onExport: () => void;
  t: (key: string) => string;
};

const BoardToolbar: React.FC<BoardToolbarProps> = ({
  filters,
  setFilter,
  clearFilters,
  activeCount,
  users,
  canExport,
  onExport,
  t,
}) => {
  const sortedUsers = [...users].sort((a, b) =>
    `${a.firstName} ${a.lastName}`.toLowerCase().localeCompare(`${b.firstName} ${b.lastName}`.toLowerCase()),
  );

  const dateRange = (key: 'createdDate' | 'startDate' | 'dueDate', label: string) => (
    <div className="filter-field">
      <div className="filter-label">{label}</div>
      <DatePicker.RangePicker
        value={rangeValue(filters[key]) as any}
        onChange={(vals) => setFilter(key, rangeParam(vals as any))}
        allowClear
        format="YYYY-MM-DD"
        style={{ width: '100%' }}
      />
    </div>
  );

  const filterPanel = (
    <div style={{ width: 640, padding: 16 }}>
      <Row gutter={16}>
        <Col span={12}>
          <div className="filter-field">
            <div className="filter-label">{t('FilterByAssignee')}</div>
            <Select
              allowClear
              showSearch
              optionFilterProp="children"
              style={{ width: '100%' }}
              value={filters.assignee}
              onChange={(value) => setFilter('assignee', value ?? undefined)}
            >
              {sortedUsers.map((user) => (
                <Option key={user.id} value={String(user.id)}>
                  {user.firstName} {user.lastName}
                </Option>
              ))}
            </Select>
          </div>
        </Col>
        <Col span={12}>
          <div className="filter-field">
            <div className="filter-label">{t('FilterByPriority')}</div>
            <Select
              allowClear
              style={{ width: '100%' }}
              value={filters.priority}
              onChange={(value) => setFilter('priority', value ?? undefined)}
            >
              {priorityOptions(t).map((opt) => (
                <Option key={opt.value} value={String(opt.value)}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </div>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginTop: 12 }}>
        <Col span={12}>{dateRange('startDate', t('StartDate'))}</Col>
        <Col span={12}>{dateRange('dueDate', t('DueDate'))}</Col>
      </Row>

      <Row gutter={16} style={{ marginTop: 12 }}>
        <Col span={12}>{dateRange('createdDate', t('CreatedAt'))}</Col>
      </Row>

      <Row justify="end" style={{ marginTop: 16 }}>
        <Button onClick={clearFilters}>{t('ClearFilters')}</Button>
      </Row>
    </div>
  );

  return (
    <div className="filter-bar">
      <Row justify="space-between" align="middle">
        <Col>
          <Space>
            <Input
              placeholder={t('SearchByTitleOrId')}
              value={filters.search}
              onChange={(e) => setFilter('search', e.target.value)}
              className="hover-effect"
            />
            <Popover trigger="click" placement="bottomLeft" overlayStyle={{ padding: 0 }} content={filterPanel}>
              <Button className="hover-effect">
                {t('Filters')}
                {activeCount > 0 && <Badge count={activeCount} style={{ marginLeft: 6 }} />}
              </Button>
            </Popover>
            {canExport && (
              <Button onClick={onExport} className="hover-effect">
                {t('ExportTime')}
              </Button>
            )}
          </Space>
        </Col>

        <Col>
          <Space>
            <Radio.Group
              value={filters.view}
              onChange={(e) => setFilter('view', e.target.value)}
              buttonStyle="solid"
              className="view-mode-toggle"
            >
              <Radio.Button value="kanban" className="hover-effect">
                <AppstoreFilled className="view-mode-icon" />
                {t('Kanban')}
              </Radio.Button>
              <Radio.Button value="list" className="hover-effect">
                <List size={16} className="view-mode-icon" />
                {t('List')}
              </Radio.Button>
            </Radio.Group>
          </Space>
        </Col>
      </Row>
    </div>
  );
};

export default BoardToolbar;
